import type { JsonObject } from "@/lib/api/types/json";

/** Logical uplink / integration stack the logger uses (stored on device metadata). */
export type TransmissionMode =
  | "mqtt"
  | "http"
  | "websocket"
  | "polling"
  | "ethernet"
  | "wifi"
  | "cellular"
  | "bluetooth_serial"
  | "modbus_tcp"
  | "lora";

/** Optional protocol-specific commissioning hints (all optional; devices may override locally). */
export type HardwareProtocolSettings = {
  mqttBroker?: string;
  mqttPort?: string;
  mqttTls?: boolean;
  mqttTopicPrefix?: string;
  mqttTopic?: string;
  mqttUsername?: string;
  mqttPassword?: string;
  mqttPublisherClientId?: string;
  httpBaseUrl?: string;
  websocketUrl?: string;
  pollIntervalSeconds?: string;
  bluetoothDeviceName?: string;
  bluetoothPort?: string;
  bluetoothBaudRate?: string;
  bluetoothDataFormat?: string;
  bluetoothLineEnding?: string;
  bluetoothNote?: string;
  lanServerHost?: string;
  lanServerPort?: string;
  lanServerUseTls?: boolean;
  ethernetDhcp?: boolean;
  ethernetIp?: string;
  ethernetGateway?: string;
  ethernetNetmask?: string;
  ethernetDns?: string;
  wifiSsid?: string;
  wifiPassword?: string;
  wifiSecurityType?: string;
  wifiUseDhcp?: boolean;
  wifiStaticIp?: string;
  wifiGateway?: string;
  wifiNetmask?: string;
  wifiDns?: string;
  cellularApn?: string;
  modbusHost?: string;
  modbusPort?: string;
  modbusUnitId?: string;
  loraRegion?: string;
  loraNetworkServer?: string;
  loraAppEui?: string;
  loraDevEui?: string;
  loraAppKey?: string;
};

export const CONNECTIVITY_PROTOCOL_OPTIONS: { value: TransmissionMode; label: string; hint: string }[] = [
  { value: "mqtt", label: "MQTT", hint: "Telemetry over MQTT broker (TLS optional)" },
  { value: "http", label: "HTTP/REST", hint: "POST or REST-style ingestion" },
  { value: "websocket", label: "WebSocket", hint: "Persistent socket uplink" },
  { value: "ethernet", label: "Ethernet (LAN)", hint: "Wired LAN with local HTTP telemetry to /telemetry/sync" },
  { value: "wifi", label: "Wi‑Fi", hint: "Wireless LAN connection" },
  { value: "bluetooth_serial", label: "Bluetooth Serial (SPP)", hint: "Classic Bluetooth serial COM-port telemetry" },
  { value: "modbus_tcp", label: "Modbus TCP", hint: "Industrial bridge / PLC segment" },
  { value: "lora", label: "LoRaWAN", hint: "Commissioning values only for now; uplink ingestion will be added later" },
];

export function connectivityProtocolLabel(mode: TransmissionMode): string {
  return CONNECTIVITY_PROTOCOL_OPTIONS.find((o) => o.value === mode)?.label ?? mode;
}

function pruneProtocolSettings(s: HardwareProtocolSettings): JsonObject | null {
  const out: JsonObject = {};
  for (const [k, v] of Object.entries(s)) {
    if (k === "mqttPassword" || k === "wifiPassword") {
      continue;
    }
    if (v === undefined || v === null) {
      continue;
    }
    if (typeof v === "string" && v.trim() === "") {
      continue;
    }
    out[k] = v as unknown as NonNullable<JsonObject[string]>;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Unit for “N samples per …” — canonical rate stored as Hz in `samplingRate`. */
export type SamplingRatePeriod = "second" | "minute" | "hour" | "day";

export const SAMPLING_RATE_PERIOD_OPTIONS: { value: SamplingRatePeriod; label: string }[] = [
  { value: "second", label: "Per second" },
  { value: "minute", label: "Per minute" },
  { value: "hour", label: "Per hour" },
  { value: "day", label: "Per day" },
];

const PERIOD_SECONDS: Record<SamplingRatePeriod, number> = {
  second: 1,
  minute: 60,
  hour: 3600,
  day: 86400,
};

/** Converts “value samples per period” to Hz (samples per second). */
export function samplesPerPeriodToHz(value: number, period: SamplingRatePeriod): number {
  const denom = PERIOD_SECONDS[period];
  return value / denom;
}

export function formatSamplingRateHz(hz: number): string {
  if (!Number.isFinite(hz) || hz <= 0) {
    return "—";
  }
  if (hz >= 100) {
    return `${hz.toFixed(0)} Hz`;
  }
  if (hz >= 1) {
    return `${hz.toFixed(2)} Hz`;
  }
  if (hz >= 0.0001) {
    return `${hz.toFixed(4)} Hz`;
  }
  return `${hz.toExponential(2)} Hz`;
}

export type DataType = "float" | "int" | "bool" | "string" | "formula";

export type ChannelFormulaOperator = "add" | "subtract" | "multiply" | "divide";

export type ChannelFormula = {
  /** Target channel key to read from (must not be this channel). */
  sourceChannelKey: string;
  operator: ChannelFormulaOperator;
  operand: number;
};

export type GraphType =
  | "line"
  | "bar"
  | "area"
  | "scatter"
  | "gauge"
  | "step"
  | "histogram"
  | "stacked_bar"
  | "donut"
  | "heatmap"
  | "radar"
  | "candlestick"
  | "ohlc"
  | "gantt"
  | "table";

export const GRAPH_TYPE_OPTIONS: { value: GraphType; label: string }[] = [
  { value: "line", label: "Line" },
  { value: "area", label: "Area" },
  { value: "bar", label: "Bar" },
  { value: "stacked_bar", label: "Stacked bar" },
  { value: "histogram", label: "Histogram" },
  { value: "step", label: "Step" },
  { value: "scatter", label: "Scatter" },
  { value: "gauge", label: "Gauge" },
  { value: "donut", label: "Donut" },
  { value: "heatmap", label: "Heatmap" },
  { value: "radar", label: "Radar" },
  { value: "candlestick", label: "Candlestick" },
  { value: "ohlc", label: "OHLC" },
  { value: "gantt", label: "Gantt" },
  { value: "table", label: "Table" },
];

export const LIVE_READY_GRAPH_TYPES = [
  "line",
  "area",
  "bar",
  "step",
  "scatter",
  "gauge",
  "histogram",
  "candlestick",
  "ohlc",
  "gantt",
  "table",
] as const satisfies readonly GraphType[];

export const LIVE_READY_GRAPH_TYPE_OPTIONS = GRAPH_TYPE_OPTIONS.filter((option) =>
  (LIVE_READY_GRAPH_TYPES as readonly GraphType[]).includes(option.value),
);

export const DEFERRED_GRAPH_TYPE_OPTIONS = GRAPH_TYPE_OPTIONS.filter(
  (option) => !(LIVE_READY_GRAPH_TYPES as readonly GraphType[]).includes(option.value),
);
export type FloatPrecision = 1 | 2 | 3;

export type ChannelAggregation = "avg" | "min" | "max" | "sum" | "last" | "count";

export type BoolExpected = "any" | "true" | "false";

export const DATA_TYPE_OPTIONS: {
  value: DataType;
  label: string;
}[] = [
  { value: "float", label: "Float" },
  { value: "int", label: "Integer" },
  { value: "bool", label: "Boolean" },
  { value: "string", label: "String" },
  { value: "formula", label: "Formula" },
];

export const AGGREGATION_OPTIONS: { value: ChannelAggregation; label: string }[] = [
  { value: "avg", label: "Average" },
  { value: "min", label: "Minimum" },
  { value: "max", label: "Maximum / highest" },
  { value: "sum", label: "Sum" },
  { value: "last", label: "Last value" },
  { value: "count", label: "Count" },
];

const NUMERIC_GRAPH_TYPES: GraphType[] = [
  "line",
  "bar",
  "area",
  "scatter",
  "gauge",
  "step",
  "histogram",
  "stacked_bar",
  "donut",
  "heatmap",
  "radar",
  "candlestick",
  "ohlc",
  "gantt",
  "table",
];

const BOOLEAN_GRAPH_TYPES: GraphType[] = ["step", "table"];
const STRING_GRAPH_TYPES: GraphType[] = ["table"];

const NUMERIC_AGGREGATIONS: ChannelAggregation[] = ["avg", "min", "max", "sum", "last", "count"];
const BOOLEAN_AGGREGATIONS: ChannelAggregation[] = [];
const STRING_AGGREGATIONS: ChannelAggregation[] = [];

export function isNumericDataType(dataType: DataType): boolean {
  return dataType === "float" || dataType === "int" || dataType === "formula";
}

export function allowedAggregationsForDataType(dataType: DataType): ChannelAggregation[] {
  if (dataType === "bool") {
    return BOOLEAN_AGGREGATIONS;
  }
  if (dataType === "string") {
    return STRING_AGGREGATIONS;
  }
  return NUMERIC_AGGREGATIONS;
}

export function defaultAggregationsForDataType(dataType: DataType): ChannelAggregation[] {
  if (dataType === "bool" || dataType === "string") {
    return [];
  }
  return ["avg"];
}

export function allowedGraphTypesForDataType(dataType: DataType): GraphType[] {
  if (dataType === "bool") {
    return BOOLEAN_GRAPH_TYPES;
  }
  if (dataType === "string") {
    return STRING_GRAPH_TYPES;
  }
  return NUMERIC_GRAPH_TYPES;
}

export function defaultGraphTypeForDataType(dataType: DataType): GraphType {
  if (dataType === "bool") {
    return "step";
  }
  if (dataType === "string") {
    return "table";
  }
  return "line";
}

export function sanitizeAggregationsForDataType(
  dataType: DataType,
  aggregations: ChannelAggregation[],
): ChannelAggregation[] {
  const allowed = new Set(allowedAggregationsForDataType(dataType));
  const filtered = aggregations.filter((aggregation) => allowed.has(aggregation));
  if (filtered.length > 0) {
    return filtered;
  }
  return defaultAggregationsForDataType(dataType);
}

export function sanitizeGraphTypeForDataType(
  dataType: DataType,
  graphType: GraphType,
): GraphType {
  return allowedGraphTypesForDataType(dataType).includes(graphType)
    ? graphType
    : defaultGraphTypeForDataType(dataType);
}

/** Build at most two distinct aggregations from two dropdown slots (UI). */
export function mergeAggregationPair(
  primary: ChannelAggregation | "",
  secondary: ChannelAggregation | "",
): ChannelAggregation[] {
  const out: ChannelAggregation[] = [];
  if (primary && !out.includes(primary)) {
    out.push(primary);
  }
  if (secondary && !out.includes(secondary)) {
    out.push(secondary);
  }
  return out;
}

export function splitAggregationPair(aggs: ChannelAggregation[]): [ChannelAggregation | "", ChannelAggregation | ""] {
  return [aggs[0] ?? "", aggs[1] ?? ""];
}

export const FLOAT_PRECISION_OPTIONS: { value: FloatPrecision; label: string }[] = [
  { value: 1, label: ".0 (1 decimal)" },
  { value: 2, label: ".00 (2 decimals)" },
  { value: 3, label: ".000 (3 decimals)" },
];

export const FORMULA_OPERATOR_OPTIONS: { value: ChannelFormulaOperator; label: string }[] = [
  { value: "add", label: "Add (+)" },
  { value: "subtract", label: "Subtract (−)" },
  { value: "multiply", label: "Multiply (×)" },
  { value: "divide", label: "Divide (÷)" },
];

export type ChannelRow = {
  id: string;
  name: string;
  /** System-generated; unique among sibling channels. */
  channelKey: string;
  unit: string;
  dataType: DataType;
  minValue: number | null;
  maxValue: number | null;
  thresholdWarningLow: number | null;
  thresholdWarningHigh: number | null;
  boolExpected: BoolExpected;
  stringMaxLength: number | null;
  aggregations: ChannelAggregation[];
  prepend: string;
  append: string;
  /** Only meaningful for `float`; stored for metadata / UI. */
  floatPrecision: FloatPrecision;
  formula: ChannelFormula | null;
  graphType: GraphType;
};

export type DataloggerBasic = {
  name: string;
  deviceCategoryId: string;
  description: string;
  simType: "phone" | "industrial" | "other";
  phoneNumber: string;
  imeiNumber: string;
  location: string;
  city: string;
  stateId: string;
  state: string;
  tags: string[];
  /** Optional WGS84 — strings for controlled inputs; persisted as numbers in metadata when valid. */
  latitude: string;
  longitude: string;
  /** Relative server path for private device image storage. */
  deviceImagePath: string | null;
  /** Local preview URL for the current wizard session. */
  deviceImagePreviewUrl: string | null;
};

export type DataloggerHardwareState = {
  channelCount: number;
  samplingRateValue: number;
  samplingRatePeriod: SamplingRatePeriod;
  transmissionMode: TransmissionMode;
  protocolSettings: HardwareProtocolSettings;
};

export type DataloggerHardware = DataloggerHardwareState & {
  samplingRate: number;
};

export type DataloggerLifecycle = {
  installationDate: string;
  warrantyEnd: string;
  nextCalibration: string;
};

export type DataloggerAlertConditionSource = "manual" | "warning_low" | "warning_high";

export type DataloggerAlertCondition = {
  enabled: boolean;
  channelId: string | null;
  channelKey: string;
  operator: ">" | "<" | ">=" | "<=" | "=" | "==" | "!=";
  threshold: string;
  source: DataloggerAlertConditionSource;
};

export type DataloggerNotificationChannelPrefs = {
  email: boolean;
  message: boolean;
  inApp: boolean;
};

export type DataloggerAlertAudienceKey = "admin" | "distributor" | "users";

export type DataloggerAlertAudiencePrefs = DataloggerNotificationChannelPrefs & {
  selectAll: boolean;
  userIds: string[];
};

export type DataloggerAlertRecipients = {
  admin: DataloggerAlertAudiencePrefs;
  distributor: DataloggerAlertAudiencePrefs;
  users: DataloggerAlertAudiencePrefs;
  emailRecipients: string;
  messageRecipients: string;
};

export type WindRoseVisualization = {
  enabled: boolean;
  speedChannelKey: string;
  directionChannelKey: string;
};

export type DataloggerVisualizations = {
  windRose: WindRoseVisualization;
};

export type DataloggerProvisioningResult = {
  id: string;
  device_uid: string;
  serialNumber: string;
  apiKey: string;
  mqttTopic: string | null;
  mqttPublisherClientId: string | null;
  alertRulesCreated: number;
  alertRulesRequested: number;
  alertTemplateError: string | null;
};

export const QR_PUBLIC_FIELD_OPTIONS = [
  { value: "image", label: "Device image" },
  { value: "name", label: "Device name" },
  { value: "location", label: "Location" },
  { value: "status", label: "Status" },
  { value: "last_seen", label: "Last seen" },
  { value: "installation_date", label: "Installation date" },
  { value: "warranty_end", label: "Warranty end" },
  { value: "channels", label: "Configured channels" },
] as const;

export type QrPublicField = (typeof QR_PUBLIC_FIELD_OPTIONS)[number]["value"];
export type DataloggerQrPublic = { enabled: boolean; displayFields: QrPublicField[] };

export type DataloggerFormSnapshot = {
  basic: DataloggerBasic;
  hardware: DataloggerHardware;
  channels: ChannelRow[];
  lifecycle: DataloggerLifecycle;
  alerts: DataloggerAlertCondition[];
  alertRecipients: DataloggerAlertRecipients;
  visualizations: DataloggerVisualizations;
  qrPublic: DataloggerQrPublic;
};

export const CHANNEL_KEY_RE = /^[a-zA-Z][a-zA-Z0-9_]{0,63}$/;

export function slugKeyFromName(name: string): string {
  const raw = name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
  let k = raw.replace(/^_+|_+$/g, "") || "channel";
  if (!/^[a-zA-Z]/.test(k)) {
    k = `ch_${k}`;
  }
  return k.slice(0, 64);
}

/** Allocates a unique sibling `channelKey` from the display name. */
export function nextUniqueChannelKey(name: string, peerKeys: Iterable<string>): string {
  const taken = new Set(Array.from(peerKeys, (k) => k.trim()).filter(Boolean));
  let base = slugKeyFromName(name);
  if (!base || !CHANNEL_KEY_RE.test(base)) {
    base = "channel";
  }
  base = base.slice(0, 64);
  let candidate = base;
  let n = 2;
  while (taken.has(candidate)) {
    const suffix = `_${n}`;
    candidate = `${base.slice(0, Math.max(1, 64 - suffix.length))}${suffix}`;
    n += 1;
  }
  return candidate;
}

export function createDefaultChannel(index: number, existingKeys: string[] = []): ChannelRow {
  const name = `Channel ${index + 1}`;
  const channelKey = nextUniqueChannelKey(name, existingKeys);
  return {
    id: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `ch-${index}-${Date.now()}`,
    name,
    channelKey,
    unit: "",
    dataType: "float",
    minValue: null,
    maxValue: null,
    thresholdWarningLow: null,
    thresholdWarningHigh: null,
    boolExpected: "any",
    stringMaxLength: null,
    aggregations: defaultAggregationsForDataType("float"),
    prepend: "",
    append: "",
    floatPrecision: 2,
    formula: null,
    graphType: defaultGraphTypeForDataType("float"),
  };
}

export function formatFormulaExpression(f: ChannelFormula): string {
  const op =
    f.operator === "add"
      ? "+"
      : f.operator === "subtract"
        ? "−"
        : f.operator === "multiply"
          ? "×"
          : "÷";
  return `${f.sourceChannelKey} ${op} ${f.operand}`;
}

export function validateBasic(basic: DataloggerBasic): string[] {
  const err: string[] = [];
  if (!basic.name.trim()) {
    err.push("Name is required.");
  }
  const latS = basic.latitude.trim();
  const lngS = basic.longitude.trim();
  if (latS || lngS) {
    if (!latS || !lngS) {
      err.push("Enter both latitude and longitude, or clear both.");
    } else {
      const lat = Number(latS);
      const lng = Number(lngS);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        err.push("Latitude and longitude must be valid numbers.");
      } else if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        err.push("Latitude must be between −90 and 90, longitude between −180 and 180.");
      }
    }
  }
  return err;
}

export function validateConnectivityAndProtocol(hw: DataloggerHardware): string[] {
  const err: string[] = [];
  const s = hw.protocolSettings ?? {};
  const mode = hw.transmissionMode;
  const isHex = (value: string, length: number) => new RegExp(`^[A-Fa-f0-9]{${length}}$`).test(value);

  if (mode === "polling") {
    const raw = s.pollIntervalSeconds?.trim();
    if (raw) {
      const n = Number(raw);
      if (!Number.isFinite(n) || n < 1) {
        err.push("Polling: interval must be a positive number (seconds).");
      }
    }
  }

  if (mode === "modbus_tcp") {
    const host = s.modbusHost?.trim();
    const portRaw = s.modbusPort?.trim();
    if (host || portRaw) {
      if (!host) {
        err.push("Modbus TCP: host is required when port is set.");
      }
      if (portRaw) {
        const p = Number(portRaw);
        if (!Number.isFinite(p) || p < 1 || p > 65535) {
          err.push("Modbus TCP: port must be between 1 and 65535.");
        }
      }
    }
  }

  if (mode === "lora") {
    const appEui = s.loraAppEui?.trim();
    const devEui = s.loraDevEui?.trim();
    const appKey = s.loraAppKey?.trim();

    if (!devEui) {
      err.push("LoRaWAN: DevEUI is required.");
    } else if (!isHex(devEui, 16)) {
      err.push("LoRaWAN: DevEUI must be 16 hexadecimal characters.");
    }

    if (appEui && !isHex(appEui, 16)) {
      err.push("LoRaWAN: AppEUI / JoinEUI must be 16 hexadecimal characters.");
    }

    if (appKey && !isHex(appKey, 32)) {
      err.push("LoRaWAN: AppKey must be 32 hexadecimal characters.");
    }
  }

  if (mode === "mqtt") {
    const broker = s.mqttBroker?.trim();
    const portRaw = s.mqttPort?.trim();
    const topicRaw = s.mqttTopic?.trim();
    if (!broker) {
      err.push("MQTT: broker host is required.");
    }
    if (portRaw) {
      const p = Number(portRaw);
      if (!Number.isFinite(p) || p < 1 || p > 65535) {
        err.push("MQTT: broker port must be between 1 and 65535.");
      }
    }
    if (topicRaw && (topicRaw.includes("+") || topicRaw.includes("#"))) {
      err.push("MQTT: telemetry topic must be an exact topic, not a wildcard.");
    }
  }

  if (mode === "websocket") {
    const url = s.websocketUrl?.trim();
    if (!url) {
      err.push("WebSocket: URL is required.");
    } else if (!(url.startsWith("ws://") || url.startsWith("wss://"))) {
      err.push("WebSocket: URL must start with ws:// or wss://.");
    }
  }

  if (mode === "bluetooth_serial") {
    const port = s.bluetoothPort?.trim();
    const baudRaw = s.bluetoothBaudRate?.trim();
    if (!port) {
      err.push("Bluetooth: Windows COM port is required.");
    }
    if (baudRaw) {
      const baud = Number(baudRaw);
      if (!Number.isFinite(baud) || baud < 1 || baud > 4000000) {
        err.push("Bluetooth: baud rate must be between 1 and 4000000.");
      }
    }
  }

  if (mode === "ethernet" || mode === "wifi") {
    return err;
  }

  return err;
}

export function validateHardware(hw: DataloggerHardware): string[] {
  const err: string[] = [];
  if (!Number.isFinite(hw.channelCount) || hw.channelCount < 1) {
    err.push("Channel count must be at least 1.");
  }
  if (!Number.isFinite(hw.samplingRateValue) || hw.samplingRateValue <= 0) {
    err.push("Sampling rate value must be greater than zero.");
  }
  const hz = samplesPerPeriodToHz(hw.samplingRateValue, hw.samplingRatePeriod);
  if (!Number.isFinite(hz) || hz <= 0) {
    err.push("Effective sampling rate must be greater than zero.");
  }
  err.push(...validateConnectivityAndProtocol(hw));
  return err;
}

export function validateChannels(channels: ChannelRow[]): string[] {
  const err: string[] = [];
  if (channels.length < 1) {
    err.push("At least one channel is required.");
  }
  const keys = new Map<string, number>();
  const keySet = new Set(channels.map((c) => c.channelKey.trim()));

  for (let i = 0; i < channels.length; i += 1) {
    const ch = channels[i]!;
    if (!ch.name.trim()) {
      err.push(`Channel ${i + 1}: name is required.`);
    }
    const k = ch.channelKey.trim();
    if (!k) {
      err.push(`Channel ${i + 1}: a system key could not be assigned.`);
    } else if (!CHANNEL_KEY_RE.test(k)) {
      err.push(`Channel ${i + 1}: key must start with a letter and contain only letters, digits, or underscore (max 64).`);
    } else {
      keys.set(k, (keys.get(k) ?? 0) + 1);
    }

    const numericLike = isNumericDataType(ch.dataType);
    if (numericLike && ch.minValue != null && ch.maxValue != null && ch.minValue >= ch.maxValue) {
      err.push(`Channel ${i + 1}: minimum must be less than maximum when both are set.`);
    }
    if (
      numericLike &&
      ch.thresholdWarningLow != null &&
      ch.thresholdWarningHigh != null &&
      ch.thresholdWarningLow >= ch.thresholdWarningHigh
    ) {
      err.push(`Channel ${i + 1}: warning low must be less than warning high when both are set.`);
    }

    if (ch.dataType === "string" && ch.stringMaxLength != null && ch.stringMaxLength < 1) {
      err.push(`Channel ${i + 1}: max length must be at least 1 when set.`);
    }

    const allowedAggregations = new Set(allowedAggregationsForDataType(ch.dataType));
    const invalidAggregation = ch.aggregations.find((aggregation) => !allowedAggregations.has(aggregation));
    if (invalidAggregation) {
      err.push(`Channel ${i + 1}: aggregation "${invalidAggregation}" is not supported for ${ch.dataType} channels.`);
    }

    if (!allowedGraphTypesForDataType(ch.dataType).includes(ch.graphType)) {
      err.push(`Channel ${i + 1}: graph type "${ch.graphType}" is not supported for ${ch.dataType} channels.`);
    }

    if (ch.dataType === "formula") {
      if (!ch.formula) {
        err.push(`Channel ${i + 1}: open “Configure formula” and define the expression.`);
      } else {
        const { sourceChannelKey, operator, operand } = ch.formula;
        if (!sourceChannelKey || !keySet.has(sourceChannelKey)) {
          err.push(`Channel ${i + 1}: formula must reference another channel that exists.`);
        } else if (sourceChannelKey === ch.channelKey) {
          err.push(`Channel ${i + 1}: formula cannot reference this channel’s own key.`);
        }
        if (!["add", "subtract", "multiply", "divide"].includes(operator)) {
          err.push(`Channel ${i + 1}: invalid formula operator.`);
        }
        if (!Number.isFinite(operand)) {
          err.push(`Channel ${i + 1}: formula operand must be a finite number.`);
        }
        if (operator === "divide" && operand === 0) {
          err.push(`Channel ${i + 1}: cannot divide by zero in formula.`);
        }
      }
    }
  }

  for (const [k, count] of keys) {
    if (count > 1) {
      err.push(`Duplicate channel key: ${k}`);
      break;
    }
  }
  return err;
}

export function normalizeAlertCondition(
  alert: DataloggerAlertCondition,
  channels: ChannelRow[],
): DataloggerAlertCondition {
  return {
    ...alert,
    channelId:
      alert.channelId && channels.some((c) => c.id === alert.channelId)
        ? alert.channelId
        : channels.find((c) => c.channelKey === alert.channelKey)?.id ?? channels[0]?.id ?? null,
    channelKey: channels.some((c) => c.channelKey === alert.channelKey)
      ? alert.channelKey
      : channels[0]?.channelKey ?? "",
  };
}

export function validateAlertCondition(alert: DataloggerAlertCondition, channels: ChannelRow[]): string[] {
  if (!alert.enabled) {
    return [];
  }

  const err: string[] = [];
  if (!alert.channelKey.trim()) {
    err.push("Alert channel is required.");
  } else if (!channels.some((c) => c.channelKey === alert.channelKey.trim())) {
    err.push("Alert channel must match a configured channel.");
  }
  if (!Number.isFinite(Number(alert.threshold))) {
    err.push("Alert threshold must be a valid number.");
  }
  return err;
}

export function validateAlertTemplates(alerts: DataloggerAlertCondition[], channels: ChannelRow[]): string[] {
  return alerts.flatMap((alert, index) =>
    validateAlertCondition(normalizeAlertCondition(alert, channels), channels).map((message) => `Alert ${index + 1}: ${message}`),
  );
}

export function validateVisualizations(visualizations: DataloggerVisualizations, channels: ChannelRow[]): string[] {
  const err: string[] = [];
  const windRose = visualizations.windRose;
  if (!windRose.enabled) {
    return err;
  }

  const numericChannels = new Set(
    channels
      .filter((channel) => channel.dataType === "float" || channel.dataType === "int" || channel.dataType === "formula")
      .map((channel) => channel.channelKey),
  );

  if (!windRose.speedChannelKey.trim()) {
    err.push("Wind rose: wind speed channel is required.");
  }
  if (!windRose.directionChannelKey.trim()) {
    err.push("Wind rose: wind direction channel is required.");
  }
  if (
    windRose.speedChannelKey.trim() &&
    windRose.directionChannelKey.trim() &&
    windRose.speedChannelKey.trim() === windRose.directionChannelKey.trim()
  ) {
    err.push("Wind rose: speed and direction channels must be different.");
  }
  if (windRose.speedChannelKey.trim() && !numericChannels.has(windRose.speedChannelKey.trim())) {
    err.push("Wind rose: selected wind speed channel must exist and be numeric.");
  }
  if (windRose.directionChannelKey.trim() && !numericChannels.has(windRose.directionChannelKey.trim())) {
    err.push("Wind rose: selected wind direction channel must exist and be numeric.");
  }
  return err;
}

export function validateStep(stepIndex: number, snapshot: DataloggerFormSnapshot): string[] {
  switch (stepIndex) {
    case 0:
      return [...validateBasic(snapshot.basic), ...validateConnectivityAndProtocol(snapshot.hardware)];
    case 1:
      return validateChannels(snapshot.channels);
    case 2:
      return validateVisualizations(snapshot.visualizations, snapshot.channels);
    case 3:
      return validateHardware(snapshot.hardware);
    case 4:
      return validateAlertTemplates(snapshot.alerts, snapshot.channels);
    case 5:
      return validateConnectivityAndProtocol(snapshot.hardware);
    case 6:
      return [];
    case 7:
      return [
        ...validateBasic(snapshot.basic),
        ...validateHardware(snapshot.hardware),
        ...validateChannels(snapshot.channels),
        ...validateAlertTemplates(snapshot.alerts, snapshot.channels),
        ...validateVisualizations(snapshot.visualizations, snapshot.channels),
      ];
    default:
      return [];
  }
}

/** Build metadata object stored on device (API-safe JSON). */
export function snapshotToMetadata(snapshot: DataloggerFormSnapshot): JsonObject {
  const latStr = snapshot.basic.latitude.trim();
  const lngStr = snapshot.basic.longitude.trim();
  const lat = latStr !== "" ? Number(latStr) : NaN;
  const lng = lngStr !== "" ? Number(lngStr) : NaN;

  const protocolPruned = pruneProtocolSettings(snapshot.hardware.protocolSettings ?? {});

  const meta: JsonObject = {
    schema_version: 2,
    kind: "datalogger",
    basic: {
      name: snapshot.basic.name,
      description: snapshot.basic.description,
      sim_type: snapshot.basic.simType,
      phone_number: snapshot.basic.phoneNumber.trim() || null,
      imei_number: snapshot.basic.imeiNumber.trim() || null,
      location: snapshot.basic.location,
      city: snapshot.basic.city,
      state_id: snapshot.basic.stateId.trim() || null,
      state: snapshot.basic.state.trim() || null,
      tags: snapshot.basic.tags,
    },
    hardware: {
      channelCount: snapshot.hardware.channelCount,
      samplingRateValue: snapshot.hardware.samplingRateValue,
      samplingRatePeriod: snapshot.hardware.samplingRatePeriod,
      samplingRateHz: snapshot.hardware.samplingRate,
      transmissionMode: snapshot.hardware.transmissionMode,
      ...(protocolPruned ? { protocolSettings: protocolPruned } : {}),
    },
    alerts: {
      defaults: {
        evaluationWindow: "last_1h",
        aggregation: "avg",
        evaluationMode: "latest_value",
        severity: "warning",
      },
      recipients: {
        notifyAdmin:
          snapshot.alertRecipients.admin.email ||
          snapshot.alertRecipients.admin.message ||
          snapshot.alertRecipients.admin.inApp,
        notifyDistributor:
          snapshot.alertRecipients.distributor.email ||
          snapshot.alertRecipients.distributor.message ||
          snapshot.alertRecipients.distributor.inApp,
        notifyUsers:
          snapshot.alertRecipients.users.email ||
          snapshot.alertRecipients.users.message ||
          snapshot.alertRecipients.users.inApp,
        enableEmail:
          snapshot.alertRecipients.admin.email ||
          snapshot.alertRecipients.distributor.email ||
          snapshot.alertRecipients.users.email,
        enableMessage:
          snapshot.alertRecipients.admin.message ||
          snapshot.alertRecipients.distributor.message ||
          snapshot.alertRecipients.users.message,
        admin: snapshot.alertRecipients.admin,
        distributor: snapshot.alertRecipients.distributor,
        users: snapshot.alertRecipients.users,
        emailRecipients: snapshot.alertRecipients.emailRecipients
          ? snapshot.alertRecipients.emailRecipients
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
          : [],
        messageRecipients: snapshot.alertRecipients.messageRecipients
          ? snapshot.alertRecipients.messageRecipients
              .split(",")
              .map((v) => v.trim())
              .filter(Boolean)
          : [],
      },
      conditions: snapshot.alerts.map((alert) => ({
      enabled: alert.enabled,
      channelId: alert.channelId,
      channelKey: alert.channelKey.trim(),
      operator: alert.operator,
      threshold: alert.threshold.trim(),
      source: alert.source,
    })),
    },
    channels: snapshot.channels.map((c) => ({
      id: c.id,
      name: c.name,
      key: c.channelKey,
      unit: c.unit,
      dataType: c.dataType,
      minValue: c.minValue,
      maxValue: c.maxValue,
      thresholdWarningLow: c.thresholdWarningLow,
      thresholdWarningHigh: c.thresholdWarningHigh,
      boolExpected: c.boolExpected,
      stringMaxLength: c.stringMaxLength,
      aggregations: c.aggregations,
      prepend: c.prepend,
      append: c.append,
      floatPrecision: c.floatPrecision,
      formula: c.formula,
      graphType: c.graphType,
    })),
    visualizations: {
      wind_rose: {
        enabled: snapshot.visualizations.windRose.enabled,
        speed_channel_key: snapshot.visualizations.windRose.speedChannelKey.trim() || null,
        direction_channel_key: snapshot.visualizations.windRose.directionChannelKey.trim() || null,
      },
    },
  };

  const installationDate = snapshot.lifecycle.installationDate.trim();
  if (installationDate) {
    meta.installation_date = installationDate;
  }
  const warrantyEnd = snapshot.lifecycle.warrantyEnd.trim();
  if (warrantyEnd) {
    meta.warranty_end = warrantyEnd;
  }
  const nextCalibration = snapshot.lifecycle.nextCalibration.trim();
  if (nextCalibration) {
    meta.next_calibration = nextCalibration;
  }

  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    meta.latitude = lat;
    meta.longitude = lng;
  }

  if (snapshot.basic.stateId.trim()) {
    meta.state_id = snapshot.basic.stateId.trim();
    meta.state = snapshot.basic.state.trim() || null;
  }

  if (snapshot.basic.deviceImagePath) {
    meta.device_photo_path = snapshot.basic.deviceImagePath;
  }

  return meta;
}
